
import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { daypartDataBySegment, segmentColors } from './daypartData';

const DaypartActivityChart = ({ segmentId }) => {
  const [chartType, setChartType] = useState('area');
  const daypartData = daypartDataBySegment[segmentId] || daypartDataBySegment.urban;
  const colors = segmentColors[segmentId] || segmentColors.urban;
  
  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-md shadow-md">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <div className="mt-2 space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center">
                <div 
                  className="w-3 h-3 mr-2" 
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-xs text-gray-700">{entry.name}: </span>
                <span className="text-xs font-medium text-gray-900 ml-1">
                  {entry.value} visits
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-medium text-gray-900">Customer Activity by Time of Day</h3>
        <div className="flex space-x-2">
          <button
            className={`px-3 py-1 text-xs rounded-md ${
              chartType === 'area' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setChartType('area')}
          >
            Area
          </button>
          <button
            className={`px-3 py-1 text-xs rounded-md ${
              chartType === 'bar' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setChartType('bar')}
          >
            Bar
          </button>
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'area' ? (
            <AreaChart
              data={daypartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
            >
              <defs>
                {Object.entries(colors).map(([key, color]) => (
                  <linearGradient key={key} id={`color-${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={color} stopOpacity={0.2}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="hour" 
                tick={{ fontSize: 10 }}
                interval={2}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                align="center"
                verticalAlign="bottom"
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
              />
              {segmentId === 'urban' && (
                <>
                  <Area 
                    type="monotone" 
                    dataKey="residential" 
                    name="Local Residential" 
                    stackId="1" 
                    stroke={colors.residential}
                    fillOpacity={1}
                    fill={`url(#color-residential)`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commercial" 
                    name="Commercial" 
                    stackId="1" 
                    stroke={colors.commercial}
                    fillOpacity={1}
                    fill={`url(#color-commercial)`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commuters" 
                    name="Commuters" 
                    stackId="1" 
                    stroke={colors.commuters}
                    fillOpacity={1}
                    fill={`url(#color-commuters)`}
                  />
                </>
              )}
              
              {segmentId === 'highway' && (
                <>
                  <Area 
                    type="monotone" 
                    dataKey="residential" 
                    name="Local Travelers" 
                    stackId="1" 
                    stroke={colors.residential}
                    fillOpacity={1}
                    fill={`url(#color-residential)`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commercial" 
                    name="Commercial Trucks" 
                    stackId="1" 
                    stroke={colors.commercial}
                    fillOpacity={1}
                    fill={`url(#color-commercial)`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commuters" 
                    name="Long-Distance Travelers" 
                    stackId="1" 
                    stroke={colors.commuters}
                    fillOpacity={1}
                    fill={`url(#color-commuters)`}
                  />
                </>
              )}
              
              {segmentId === 'rural' && (
                <>
                  <Area 
                    type="monotone" 
                    dataKey="residential" 
                    name="Local Residents" 
                    stackId="1" 
                    stroke={colors.residential}
                    fillOpacity={1}
                    fill={`url(#color-residential)`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commercial" 
                    name="Agricultural" 
                    stackId="1" 
                    stroke={colors.commercial}
                    fillOpacity={1}
                    fill={`url(#color-commercial)`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commuters" 
                    name="Travelers" 
                    stackId="1" 
                    stroke={colors.commuters}
                    fillOpacity={1}
                    fill={`url(#color-commuters)`}
                  />
                </>
              )}
              
              {segmentId === 'suburban' && (
                <>
                  <Area 
                    type="monotone" 
                    dataKey="residential" 
                    name="Local Families" 
                    stackId="1" 
                    stroke={colors.residential}
                    fillOpacity={1}
                    fill={`url(#color-residential)`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commercial" 
                    name="Neighborhood Businesses" 
                    stackId="1" 
                    stroke={colors.commercial}
                    fillOpacity={1}
                    fill={`url(#color-commercial)`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="commuters" 
                    name="Commuters" 
                    stackId="1" 
                    stroke={colors.commuters}
                    fillOpacity={1}
                    fill={`url(#color-commuters)`}
                  />
                </>
              )}
            </AreaChart>
          ) : (
            <BarChart
              data={daypartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="hour" 
                tick={{ fontSize: 10 }}
                interval={2}
              />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                align="center"
                verticalAlign="bottom"
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
              />
              {segmentId === 'urban' && (
                <>
                  <Bar 
                    dataKey="residential" 
                    name="Local Residential" 
                    stackId="a" 
                    fill={colors.residential}
                  />
                  <Bar 
                    dataKey="commercial" 
                    name="Commercial" 
                    stackId="a" 
                    fill={colors.commercial}
                  />
                  <Bar 
                    dataKey="commuters" 
                    name="Commuters" 
                    stackId="a" 
                    fill={colors.commuters}
                  />
                </>
              )}
              
              {segmentId === 'highway' && (
                <>
                  <Bar 
                    dataKey="residential" 
                    name="Local Travelers" 
                    stackId="a" 
                    fill={colors.residential}
                  />
                  <Bar 
                    dataKey="commercial" 
                    name="Commercial Trucks" 
                    stackId="a" 
                    fill={colors.commercial}
                  />
                  <Bar 
                    dataKey="commuters" 
                    name="Long-Distance Travelers" 
                    stackId="a" 
                    fill={colors.commuters}
                  />
                </>
              )}
              
              {segmentId === 'rural' && (
                <>
                  <Bar 
                    dataKey="residential" 
                    name="Local Residents" 
                    stackId="a" 
                    fill={colors.residential}
                  />
                  <Bar 
                    dataKey="commercial" 
                    name="Agricultural" 
                    stackId="a" 
                    fill={colors.commercial}
                  />
                  <Bar 
                    dataKey="commuters" 
                    name="Travelers" 
                    stackId="a" 
                    fill={colors.commuters}
                  />
                </>
              )}
              
              {segmentId === 'suburban' && (
                <>
                  <Bar 
                    dataKey="residential" 
                    name="Local Families" 
                    stackId="a" 
                    fill={colors.residential}
                  />
                  <Bar 
                    dataKey="commercial" 
                    name="Neighborhood Businesses" 
                    stackId="a" 
                    fill={colors.commercial}
                  />
                  <Bar 
                    dataKey="commuters" 
                    name="Commuters" 
                    stackId="a" 
                    fill={colors.commuters}
                  />
                </>
              )}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      
      <div className="mt-3 text-xs text-gray-500 italic">
        <p className="text-center">Click the legend items to toggle data series visibility</p>
      </div>
    </div>
  );
};

export default DaypartActivityChart;
